import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';
/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @returns {HTMLElement | null} */
  #getThemeDrawer() {
    return this.isDrawer ? document.getElementById('cart-drawer') : null;
  }

  /**
   * Re-syncs the native dialog open state after a Section Rendering API morph.
   * @param {HTMLElement} drawer
   */
  #restoreDrawerOpenState(drawer) {
    const panel = drawer.querySelector('dialog');
    const themeDrawer = /** @type {import('@theme/theme-drawer').ThemeDrawer} */ (drawer);

    if (!drawer.hasAttribute('open')) {
      themeDrawer.open?.();
      return;
    }

    if (panel instanceof HTMLDialogElement && !panel.open) {
      const isModal = drawer.hasAttribute('overlay') || window.matchMedia('(max-width: 989px)').matches;
      if (isModal) {
        panel.showModal();
      } else {
        panel.show();
      }
    }
  }

  /**
   * Morphs this section and preserves an open cart drawer across the update.
   * @param {string} sectionHtml
   * @param {object} options
   * @param {'hydration' | 'full'} options.mode
   * @param {boolean} [options.injectStylesheet]
   */
  async #morphCartSection(sectionHtml, options) {
    const drawer = this.#getThemeDrawer();
    const panel = drawer?.querySelector('dialog');
    const wasDrawerOpen =
      (drawer?.hasAttribute('open') ?? false) ||
      (panel instanceof HTMLDialogElement && panel.open);

    sectionRenderer.abortRender(this.sectionId);
    await morphSection(this.sectionId, sectionHtml, options);

    if (wasDrawerOpen && drawer?.isConnected) {
      this.#restoreDrawerOpenState(drawer);
    }
  }

  /**
   * @param {string | undefined} sectionHtml
   * @param {{ mode: 'hydration' | 'full', injectStylesheet?: boolean }} options
   */
  async #morphSectionIfNeeded(sectionHtml, options) {
    if (!sectionHtml) return;

    if (this.isDrawer) {
      await this.#morphCartSection(sectionHtml, options);
      return;
    }

    sectionRenderer.abortRender(this.sectionId);
    await morphSection(this.sectionId, sectionHtml, options);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          this.#morphSectionIfNeeded(sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' }).then(() => {
            this.#updateCartQuantitySelectorButtonStates();
          });
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          this.#morphSectionIfNeeded(sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId = this.refs.cartItemRows[line - 1]?.dataset.key ?? '';
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        const cartErrorMessage = this.#getCartErrorMessage(parsedResponseText);

        if (cartErrorMessage) {
          const hydratedResponse = await this.#hydrateCartChangeErrorResponse(parsedResponseText);
          const sectionsHtml = hydratedResponse.sections?.[this.sectionId];
          const parsedItemCount = Number(hydratedResponse.item_count);
          let newCartItemCount = Number.isFinite(parsedItemCount) ? parsedItemCount : 0;
          const hasUpdatedCart = this.#hasPartialCartSuccess(hydratedResponse, sectionsHtml);

          // 422 partial inventory success: Shopify updates the cart server-side but
          // still returns errors (e.g. "Only 10 items were added..."). The change
          // response may omit `items`, so hydrate from /cart.json when needed.
          if (hasUpdatedCart) {
            if (sectionsHtml) {
              const newSectionHTML = new DOMParser().parseFromString(sectionsHtml, 'text/html');
              const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
              if (newCartHiddenItemCount) {
                newCartItemCount = parseInt(newCartHiddenItemCount, 10) || newCartItemCount;
              }
            }

            // Sync before resolving so cartLinesUpdate listeners (and hydration morph
            // microtasks) cannot clobber quantity inputs with stale form state.
            await this.#syncPartialCartSuccess(line, hydratedResponse, sectionsHtml, cartErrorMessage);

            deferredUpdatePromise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(hydratedResponse),
              detail: {
                sections: hydratedResponse.sections,
                items: hydratedResponse.items,
                itemCount: newCartItemCount,
                source: 'cart-items-component',
                didError: true,
              },
            });
          } else {
            this.#handleCartError(line, hydratedResponse, cartErrorMessage);
            deferredUpdatePromise.reject(new Error(cartErrorMessage));
          }

          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        const sectionsHtml = parsedResponseText.sections?.[this.sectionId];

        if (sectionsHtml) {
          await this.#morphSectionIfNeeded(sectionsHtml, {
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Extracts a cart error message from Cart AJAX error payloads.
   * @param {Object} parsedResponseText
   * @returns {string | null}
   */
  #getCartErrorMessage(parsedResponseText) {
    if (typeof parsedResponseText.errors === 'string' && parsedResponseText.errors) {
      return parsedResponseText.errors;
    }

    if (parsedResponseText.errors) {
      return JSON.stringify(parsedResponseText.errors);
    }

    if (parsedResponseText.status === 422) {
      if (typeof parsedResponseText.description === 'string' && parsedResponseText.description) {
        return parsedResponseText.description;
      }

      if (typeof parsedResponseText.message === 'string' && parsedResponseText.message) {
        return parsedResponseText.message;
      }
    }

    return null;
  }

  /**
   * Hydrates a cart/change error response with cart JSON when `items` is omitted.
   * @param {Object} parsedResponseText
   * @returns {Promise<Object>}
   */
  async #hydrateCartChangeErrorResponse(parsedResponseText) {
    if (Array.isArray(parsedResponseText.items)) return parsedResponseText;

    try {
      const cartData = await this.fetchCartData();
      if (!Array.isArray(cartData?.items)) return parsedResponseText;

      return {
        ...cartData,
        ...parsedResponseText,
        items: cartData.items,
        item_count: cartData.item_count ?? parsedResponseText.item_count,
      };
    } catch (error) {
      console.error(error);
      return parsedResponseText;
    }
  }

  /**
   * True when a cart/change error response still reflects an updated cart state.
   * @param {Object} parsedResponseText
   * @param {string | undefined} sectionsHtml
   * @returns {boolean}
   */
  #hasPartialCartSuccess(parsedResponseText, sectionsHtml) {
    const itemCount = Number(parsedResponseText.item_count);

    if (Array.isArray(parsedResponseText.items)) {
      return parsedResponseText.items.length > 0 || (Number.isFinite(itemCount) && itemCount > 0);
    }

    return (Number.isFinite(itemCount) && itemCount > 0) || Boolean(sectionsHtml);
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} errorMessage - The error message.
   */
  #handleCartError = (line, parsedResponseText, errorMessage) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;
    this.#showCartItemError(line, errorMessage);
  };

  /**
   * Syncs UI after a 422 partial inventory success.
   * @param {number} line - The 1-based cart line index.
   * @param {Object} parsedResponseText - The parsed Cart AJAX response.
   * @param {string | undefined} sectionsHtml - Rendered section HTML, when provided.
   * @param {string} errorMessage - The cart error message.
   */
  async #syncPartialCartSuccess(line, parsedResponseText, sectionsHtml, errorMessage) {
    const patchedSectionsHtml = sectionsHtml
      ? this.#patchSectionHtmlWithCartQuantities(sectionsHtml, parsedResponseText.items)
      : undefined;

    const releaseMorphGuard = this.#guardQuantityInputsDuringMorph();

    try {
      if (patchedSectionsHtml) {
        await this.#morphSectionIfNeeded(patchedSectionsHtml, {
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      } else {
        await sectionRenderer.renderSection(this.sectionId, {
          cache: false,
          mode: this.isDrawer ? 'hydration' : 'full',
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      releaseMorphGuard();
    }

    this.#applyPartialCartQuantities(line, parsedResponseText);
    this.#showCartItemError(line, errorMessage);
  }

  /**
   * Applies server cart quantities to line inputs after a partial inventory update.
   * @param {number} line - The 1-based cart line index.
   * @param {Object} parsedResponseText - The parsed Cart AJAX response.
   */
  #applyPartialCartQuantities(line, parsedResponseText) {
    this.updatedCallback();
    this.#syncCartLineQuantity(line, parsedResponseText);
    this.#updateQuantitySelectors(parsedResponseText);
    this.#updateCartQuantitySelectorButtonStates();

    if (!this.isDrawer) return;

    // Drawer hydration morph can replay stale input values via isEqualNode +
    // syncFormControlsInSubtree, and component updatedCallbacks settle in later
    // microtasks — re-sync after morph + component ref updates have settled.
    queueMicrotask(() => {
      queueMicrotask(() => {
        requestAnimationFrame(() => {
          this.updatedCallback();
          this.#syncCartLineQuantity(line, parsedResponseText);
          this.#updateQuantitySelectors(parsedResponseText);
          this.#updateCartQuantitySelectorButtonStates();
        });
      });
    });
  }

  /**
   * Prevents morph from replaying stale quantity input values during partial inventory sync.
   * @returns {() => void} Releases the guard.
   */
  #guardQuantityInputsDuringMorph() {
    const inputs = this.querySelectorAll('cart-quantity-selector-component input');
    for (const input of inputs) {
      input.setAttribute('data-skip-node-update', '');
    }

    return () => {
      for (const input of inputs) {
        input.removeAttribute('data-skip-node-update');
      }
    };
  }

  /**
   * Patches section HTML so morph receives server cart quantities instead of stale line values.
   * @param {string} sectionsHtml
   * @param {Array<{key?: string, quantity?: number | string}> | undefined} items
   * @returns {string}
   */
  #patchSectionHtmlWithCartQuantities(sectionsHtml, items) {
    if (!items?.length) return sectionsHtml;

    const doc = new DOMParser().parseFromString(sectionsHtml, 'text/html');

    items.forEach((item, index) => {
      const quantity = this.#normalizeCartQuantity(item.quantity);
      if (quantity === null) return;

      const line = index + 1;
      const quantityString = String(quantity);
      const input =
        doc.querySelector(`input[data-cart-line="${line}"]`) ??
        (item.key ? doc.querySelector(`[data-key="${item.key}"] input[type="number"]`) : null);

      if (!(input instanceof HTMLInputElement)) return;

      input.setAttribute('value', quantityString);
      input.value = quantityString;
      input.setAttribute('data-cart-quantity', quantityString);
      // Match live DOM guard so morph's form-control sync skips stale section values.
      input.setAttribute('data-skip-node-update', '');
    });

    const section =
      doc.querySelector('.shopify-section') ??
      doc.getElementById(`shopify-section-${this.sectionId}`) ??
      doc.body.firstElementChild;

    return section instanceof Element ? section.outerHTML : sectionsHtml;
  }

  /**
   * @param {number | string | null | undefined} quantity
   * @returns {number | null}
   */
  #normalizeCartQuantity(quantity) {
    const normalized = Number(quantity);
    return Number.isFinite(normalized) ? normalized : null;
  }

  /**
   * Resolves a cart line item from the Cart AJAX API response.
   * @param {number} line
   * @param {Object} parsedResponseText
   * @param {Array<{key?: string, quantity?: number | string}>} [parsedResponseText.items]
   * @returns {{key?: string, quantity?: number | string} | undefined}
   */
  #resolveCartLineItem(line, parsedResponseText) {
    const lineRow = this.refs.cartItemRows?.[line - 1];
    const lineKey = lineRow?.dataset.key;

    return (
      parsedResponseText.items?.find((item) => item.key === lineKey) ?? parsedResponseText.items?.[line - 1]
    );
  }

  /**
   * Syncs a cart line quantity input from the Cart AJAX API response.
   * @param {number} line - The 1-based cart line index.
   * @param {Object} parsedResponseText - The parsed Cart AJAX response.
   */
  #syncCartLineQuantity(line, parsedResponseText) {
    const cartItem = this.#resolveCartLineItem(line, parsedResponseText);
    const quantity = this.#normalizeCartQuantity(cartItem?.quantity);
    const quantityInput = this.#getCartLineQuantityInput(line);

    if (quantity === null) return;
    if (!(quantityInput instanceof HTMLInputElement)) return;

    const quantityString = String(quantity);
    quantityInput.value = quantityString;
    quantityInput.defaultValue = quantityString;
    quantityInput.setAttribute('data-cart-quantity', quantityString);

    const selectorComponent = quantityInput.closest('cart-quantity-selector-component');
    /** @type {any} */ (selectorComponent)?.updateButtonStates?.();
  }

  /**
   * Finds a cart line quantity input in this component's live DOM.
   * @param {number} line - The 1-based cart line index.
   * @returns {HTMLInputElement | null}
   */
  #getCartLineQuantityInput(line) {
    const selector = this.refs.quantitySelectors?.[line - 1];
    const refInput = selector?.querySelector('input[ref="quantityInput"], input[type="number"]');

    if (refInput instanceof HTMLInputElement) return refInput;

    const dataLineInput = this.querySelector(`input[data-cart-line="${line}"]`);
    if (dataLineInput instanceof HTMLInputElement) return dataLineInput;

    return this.querySelector(`cart-quantity-selector-component input[data-cart-line="${line}"]`);
  }

  /**
   * Shows a cart line item error message.
   * @param {number} line - The 1-based cart line index.
   * @param {string} errorMessage - The error message from the Cart AJAX API.
   */
  #showCartItemError(line, errorMessage) {
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = errorMessage;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: errorMessage || 'Cart update failed',
        code: 'INVALID',
      })
    );
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              this.#morphSectionIfNeeded(cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await this.#morphSectionIfNeeded(cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `cart-quantity-selector-component[data-variant-id="${variantId}"], quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if (selector.tagName === 'CART-QUANTITY-SELECTOR-COMPONENT' && input instanceof HTMLInputElement) {
          input.value = String(item.quantity);
          input.defaultValue = String(item.quantity);
          /** @type {any} */ (selector).updateButtonStates?.();
          continue;
        }

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
